import { CommonContentTray, CommonContentTrayType } from '../../../domain/common-content-tray';
import { CarouselPageItemApiModel, CreateCarouselPageItemRequestApiModel, GenericLayoutApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import { countryTargetingMapper, platformTargetingMapper, scheduledDateMapperDomainToApi } from '../../mapping/create-tray-domain-to-api';
import { PortraitSizeForMobileType } from '../../../shared/enums/portrait-size-for-mobile-type.enum';
import { commonMapper, scheduledDateMapperApiToDomain } from '../../mapping/create-tray-api-to-domain';
import { CommonTrayFormViewModel } from './common-tray-form-view-model';
import { scheduledDateMapperDomainToViewModel, targetingMapper } from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';

// 'PortraitTray':
// 'TopTenTray':

/**
 *  DOMAIN->API
 */

const mapCommonTrayTypeToCarouselLayoutApiModel = (type: CommonContentTrayType): GenericLayoutApiModel => {
  switch (type) {
    case 'PortraitTray':
      return GenericLayoutApiModel.Portrait;
    case 'TopTenTray':
      return GenericLayoutApiModel.Top10;
  }
};

const mapPortraitLayoutSizeForMobileDomainToCarouselMobileLayoutApiModel = (
  portraitLayoutSizeForMobile: PortraitSizeForMobileType
): GenericLayoutApiModel => {
  switch (portraitLayoutSizeForMobile) {
    case PortraitSizeForMobileType.PortraitLarge:
      return GenericLayoutApiModel.Large;
    case PortraitSizeForMobileType.PortraitSmall:
      return GenericLayoutApiModel.Small;
  }
};

export const mapCommonTrayDomainModelToApiModel = (
  tray: CommonContentTray,
  pageVersionId: string,
  tenantId?: string
): CreateCarouselPageItemRequestApiModel => {
  const commonProperties = mapperPipe(tray, countryTargetingMapper, scheduledDateMapperDomainToApi, platformTargetingMapper);

  return remainderMapper(commonProperties, () => ({
    layout: mapCommonTrayTypeToCarouselLayoutApiModel(tray.type),
    pageVersionId,
    userSegmentIds: tray.userSegmentIds,
    descriptionLocalized: tray.description,
    feedId: tray.feedId,
    nameLocalized: tray.title,
    backgroundImages: null,
    seoDescriptionLocalized: undefined,
    seoTitleLocalized: undefined,
    tenantId,
    mobileLayout: mapPortraitLayoutSizeForMobileDomainToCarouselMobileLayoutApiModel(tray.portraitLayoutSizeForMobile),
  }));
};

/**
 *  API -> DOMAIN
 */
const mapCarouselLayoutApiModelToCommonTrayType = (type: GenericLayoutApiModel): CommonContentTrayType => {
  switch (type) {
    case GenericLayoutApiModel.Portrait:
      return 'PortraitTray';
    case GenericLayoutApiModel.Top10:
      return 'TopTenTray';
    case GenericLayoutApiModel.Expandable:
      return 'ExpandableTray';
  }
};

const mapPortraitLayoutSizeForMobileToDomain = (mobileLayout: GenericLayoutApiModel): PortraitSizeForMobileType => {
  switch (mobileLayout) {
    case GenericLayoutApiModel.Small:
      return PortraitSizeForMobileType.PortraitSmall;
    case GenericLayoutApiModel.Large:
      return PortraitSizeForMobileType.PortraitLarge;
  }
};

export const mapCommonTrayApiModelToDomainModel = (tray: CarouselPageItemApiModel): CommonContentTray => {
  return remainderMapper(mapperPipe(tray, commonMapper, scheduledDateMapperApiToDomain), () => ({
    title: tray.nameLocalized,
    feedId: tray.feedId,
    description: tray.descriptionLocalized,
    type: mapCarouselLayoutApiModelToCommonTrayType(tray.layout),
    countryIds: tray.countryIds,
    userSegmentIds: tray.userSegmentIds,
    portraitLayoutSizeForMobile: mapPortraitLayoutSizeForMobileToDomain(tray.mobileLayout),
  }));
};

/**
 * DOMAIN -> VIEWMODEL
 */

const mapDomainCommonContentTypeToViewModelCommonContentType = (
  tray: CommonContentTrayType
): TrayFormViewModelType.TopTenTray | TrayFormViewModelType.PortraitTray => {
  switch (tray) {
    case 'TopTenTray':
      return TrayFormViewModelType.TopTenTray;
    case 'PortraitTray':
      return TrayFormViewModelType.PortraitTray;
  }
};

export const mapCommonTrayDomainModelToViewModel = (pageItem: CommonContentTray): CommonTrayFormViewModel => {
  const commonProperties = mapperPipe(pageItem, commonMapper, targetingMapper, scheduledDateMapperDomainToViewModel);

  return remainderMapper(commonProperties, () => ({
    type: mapDomainCommonContentTypeToViewModelCommonContentType(pageItem.type),
    platformIds: pageItem.platformIds,
    title: pageItem.title,
    description: pageItem.description,
    feedId: pageItem.feedId,
    portraitLayoutSizeForMobile: pageItem.portraitLayoutSizeForMobile,
  }));
};

/**
 * VIEWMODEL -> DOMAIN
 */

const mapViewModelCommonContentTypeToDomain = (type: TrayFormViewModelType): CommonContentTrayType => {
  switch (type) {
    case TrayFormViewModelType.PortraitTray:
      return 'PortraitTray';
    case TrayFormViewModelType.TopTenTray:
      return 'TopTenTray';
  }
};

export const mapCommonTrayViewModelToDomainModel = (viewModel: CommonTrayFormViewModel): CommonContentTray => {
  return {
    id: viewModel.id,
    type: mapViewModelCommonContentTypeToDomain(viewModel.type),
    userSegmentIds: viewModel.userSegmentIds,
    countryIds: viewModel.countryIds,
    platformIds: viewModel.platformIds,
    scheduledFrom: viewModel.scheduling?.scheduledFrom ? new Date(viewModel.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: viewModel.scheduling?.scheduledTo ? new Date(viewModel.scheduling.scheduledTo).getTime() : undefined,
    title: viewModel.title,
    description: viewModel.description,
    order: undefined,
    visible: undefined,
    draggable: true,
    feedId: viewModel.feedId,
    portraitLayoutSizeForMobile: viewModel.portraitLayoutSizeForMobile,
  };
};
